import { DealListItemResponse } from 'app/console-api/deals/deals';
import { BodyText, FlexColumn, FlexRow, SvgIcon, Tooltip } from '@make-software/cspr-design';
import React from 'react';
import { StyledActionIconView } from 'app/components/common/table/styled-elements';
import { ProgressBar } from 'app/components/management/apps/deals/common/progress-bar';
import {
  ArrowButton,
  RotatingIcon,
  StyledCardRow,
  StyledTableData,
  StyledTooltip
} from 'app/components/common/common-styles';
import IconArrowDown from 'assets/icons/ic-arrow-down.svg';
import { TaskShortPreview } from 'app/components/management/apps/deals/view/task-short-preview';
import { MyDealsListItemDetailsColumn } from 'app/components/management/apps/deals/list/my-deals-list-item-details-column';
import { MyDealsListItemAppDataColumn } from 'app/components/management/apps/deals/list/my-deals-list-item-app-data-column';
import {
  formatDatetimeFull,
  formatDatetimeShort,
  truncateString
} from 'app/components/utils/measures-format';
import { SmoothCollapse } from 'app/components/management/apps/deals/list/smooth-collapse';
import { describeAutomation, getIcon } from 'app/components/common/automation/registry';
import { NavLink, generatePath } from 'react-router-dom';
import { PUBLIC_APP_VIEW_PATH } from 'app/route/paths';

export interface MyDealsListItemProps {
  deal: DealListItemResponse;
  onView: (deal: DealListItemResponse) => void;
}

export const MyDealsListItem = (props: MyDealsListItemProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const onView = () => {
    props.onView(props.deal);
  };

  return (
    <StyledCardRow key={props.deal.dealDetails.id}>
      <StyledTableData>
        <FlexColumn gap={24} align={'flex-start'}>
          <FlexRow style={{ height: '80px', flexWrap: 'wrap', minWidth: '324px' }}>
            <FlexColumn gap={8} style={{ flex: 1 }}>
              <FlexRow>
                <BodyText size={1} scale={'md'}>
                  {truncateString(props.deal.dealDetails.title, 120)}
                </BodyText>
              </FlexRow>
              <FlexRow gap={8}>
                <BodyText size={3} scale={'sm'} variation={'darkGray'}>
                  Partner:
                </BodyText>
                <BodyText size={3}>
                  <NavLink to={generatePath(PUBLIC_APP_VIEW_PATH, { appId: props.deal.targetApp.id })}>
                    {props.deal.targetApp.name}
                  </NavLink>
                </BodyText>
              </FlexRow>
            </FlexColumn>
          </FlexRow>
          <SmoothCollapse isOpen={isOpen} gap={24}>
            {props.deal.myOffer && (
              <FlexRow>
                <TaskShortPreview
                  name={props.deal.myOffer.name}
                  description={props.deal.myOffer.description}
                />
              </FlexRow>
            )}
            {props.deal.targetOffer && (
              <FlexRow>
                <TaskShortPreview
                  name={props.deal.targetOffer.name}
                  description={props.deal.targetOffer.description}
                />
              </FlexRow>
            )}
          </SmoothCollapse>
        </FlexColumn>
      </StyledTableData>
      <StyledTableData>
        <FlexColumn gap={24}>
          <FlexRow style={{ height: '80px', flexWrap: 'wrap', minWidth: '300px' }}>
            <MyDealsListItemDetailsColumn deal={props.deal}></MyDealsListItemDetailsColumn>
          </FlexRow>
          <SmoothCollapse isOpen={isOpen} gap={24}>
            {props.deal.myOffer && (
              <FlexRow>
                <MyDealsListItemAppDataColumn
                  app={props.deal.myApp}
                  deal={props.deal.dealDetails}
                  offer={props.deal.myOffer}
                />
              </FlexRow>
            )}
            {props.deal.targetOffer && (
              <FlexRow>
                <MyDealsListItemAppDataColumn
                  app={props.deal.targetApp}
                  deal={props.deal.dealDetails}
                  offer={props.deal.targetOffer}
                />
              </FlexRow>
            )}
          </SmoothCollapse>
        </FlexColumn>
      </StyledTableData>
      <StyledTableData>
        {props.deal.automations.length > 0 && (
          <FlexRow gap={8}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Automations:
            </BodyText>
            <FlexRow align={'center'} gap={4}>
              {props.deal.automations.map((automation) => (
                <Tooltip tooltipContent={describeAutomation(automation)} key={automation.id}>
                  <SvgIcon src={getIcon(automation.type)} size={18} />
                </Tooltip>
              ))}
            </FlexRow>
          </FlexRow>
        )}
        <FlexRow gap={12}>
          <BodyText size={3} scale={'sm'} variation={'darkGray'}>
            Created:
          </BodyText>
          <StyledTooltip tooltipContent={formatDatetimeFull(props.deal.dealDetails.createdAt)}>
            <BodyText size={3} scale={'sm'}>
              {formatDatetimeShort(props.deal.dealDetails.createdAt)}
            </BodyText>
          </StyledTooltip>
        </FlexRow>
        <FlexRow gap={8}>
          <BodyText size={3} scale={'sm'} variation={'darkGray'}>
            Updated:
          </BodyText>
          <StyledTooltip tooltipContent={formatDatetimeFull(props.deal.dealDetails.updatedAt)}>
            <BodyText size={3} scale={'sm'}>
              {formatDatetimeShort(props.deal.dealDetails.updatedAt)}
            </BodyText>
          </StyledTooltip>
        </FlexRow>
      </StyledTableData>
      <StyledTableData>
        <ProgressBar stats={props.deal.stats} />
      </StyledTableData>
      <StyledTableData align="right">
        <FlexRow justify="end" gap={8}>
          <StyledActionIconView onClick={onView} />
          <ArrowButton
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Collapse details' : 'Expand details'}>
            <RotatingIcon src={IconArrowDown} $open={isOpen} />
          </ArrowButton>
        </FlexRow>
      </StyledTableData>
    </StyledCardRow>
  );
};
